import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { 
  Clock, 
  Play, 
  Pause, 
  RefreshCw, 
  Trash2, 
  Edit, 
  Plus,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Activity,
  Save,
  Lock
} from "lucide-react";

interface CronJob {
  jobid: number;
  schedule: string;
  command: string;
  nodename: string;
  nodeport: number;
  database: string;
  username: string;
  active: boolean;
  jobname: string;
}

interface CronJobRun {
  runid: number;
  jobid: number;
  job_pid: number;
  database: string;
  username: string;
  command: string;
  status: string;
  return_message: string | null;
  start_time: string;
  end_time: string | null;
}

// Presets de planification cron
const SCHEDULE_PRESETS = [
  { value: '*/5 * * * *', label: 'Toutes les 5 minutes', description: 'Très fréquent' },
  { value: '*/15 * * * *', label: 'Toutes les 15 minutes', description: 'Fréquent' },
  { value: '*/30 * * * *', label: 'Toutes les 30 minutes', description: 'Demi-horaire' },
  { value: '0 * * * *', label: 'Toutes les heures', description: 'Horaire' },
  { value: '0 */2 * * *', label: 'Toutes les 2 heures', description: 'Bi-horaire' },
  { value: '0 */6 * * *', label: 'Toutes les 6 heures', description: '4 fois par jour' },
  { value: '0 0 * * *', label: 'Quotidien à minuit', description: 'Une fois par jour' },
  { value: '0 1 * * *', label: 'Quotidien à 1h', description: 'Une fois par jour' },
  { value: '0 3 * * *', label: 'Quotidien à 3h', description: 'Une fois par jour' },
  { value: '0 6 * * *', label: 'Quotidien à 6h', description: 'Une fois par jour' },
  { value: '0 9 * * *', label: 'Quotidien à 9h', description: 'Une fois par jour' },
  { value: '0 12 * * *', label: 'Quotidien à midi', description: 'Une fois par jour' },
  { value: '0 18 * * *', label: 'Quotidien à 18h', description: 'Une fois par jour' },
  { value: '0 0 * * 0', label: 'Hebdomadaire (Dimanche)', description: 'Une fois par semaine' },
  { value: '0 0 * * 1', label: 'Hebdomadaire (Lundi)', description: 'Une fois par semaine' },
  { value: '0 0 1 * *', label: 'Mensuel (1er du mois)', description: 'Une fois par mois' },
  { value: 'custom', label: 'Personnalisé', description: 'Format cron personnalisé' }
];

export function CronJobsManager() {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [cronJobs, setCronJobs] = useState<CronJob[]>([]);
  const [cronHistory, setCronHistory] = useState<CronJobRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState<CronJob | null>(null);
  const [testingJob, setTestingJob] = useState<number | null>(null);
  const [password, setPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [schedulePreset, setSchedulePreset] = useState('');
  const [editForm, setEditForm] = useState({
    jobname: '',
    schedule: '',
    command: ''
  });

  const fetchCronJobs = async () => {
    try {
      const { data, error } = await supabase.rpc('get_cron_jobs');
      
      if (error) throw error;
      
      // data est maintenant un JSON, il faut le parser si c'est une string
      const jobs = typeof data === 'string' ? JSON.parse(data) : data;
      setCronJobs(Array.isArray(jobs) ? jobs : []);
    } catch (error: any) {
      console.error('Erreur chargement cron jobs:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les cron jobs",
        variant: "destructive"
      });
    }
  };

  const fetchCronHistory = async () => {
    try {
      const { data, error } = await supabase.rpc('get_cron_history', { limit_count: 50 });
      
      if (error) {
        console.error('Erreur RPC get_cron_history:', error);
        throw error;
      }
      
      console.log('Données brutes reçues:', data, 'Type:', typeof data);
      
      // Supabase retourne déjà le JSON parsé, mais il peut être null
      let history = data;
      
      // Si data est null ou undefined, utiliser un tableau vide
      if (!history) {
        history = [];
      }
      // Si data est une string JSON, la parser
      else if (typeof history === 'string') {
        try {
          history = JSON.parse(history);
        } catch (e) {
          console.error('Erreur parsing JSON:', e);
          history = [];
        }
      }
      
      console.log('Historique récupéré:', history);
      console.log('Est un tableau?', Array.isArray(history));
      console.log('Longueur:', history?.length);
      
      const historyToSet = Array.isArray(history) ? history : [];
      console.log('Données à définir dans le state:', historyToSet);
      setCronHistory(historyToSet);
    } catch (error: any) {
      console.error('Erreur chargement historique:', error);
      toast({
        title: "Information",
        description: "Impossible de charger l'historique. Les cron jobs n'ont peut-être pas encore été exécutés.",
        variant: "default"
      });
    }
  };

  const toggleCronJob = async (jobname: string, active: boolean) => {
    try {
      const { error } = await supabase.rpc('toggle_cron_job', { 
        job_name: jobname, 
        enable: active 
      });
      
      if (error) throw error;
      
      toast({
        title: "Succès",
        description: `Cron job ${active ? 'activé' : 'désactivé'}`,
      });
      
      await fetchCronJobs();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const testCronJob = async (jobname: string, jobid: number) => {
    setTestingJob(jobid);
    try {
      const job = cronJobs.find(j => j.jobid === jobid);
      if (!job) throw new Error("Job non trouvé");

      // Vérifier si c'est un appel HTTP (edge function) ou une commande SQL directe
      const urlMatch = job.command.match(/url\s*:=\s*'([^']+)'/);
      
      if (urlMatch) {
        // C'est un appel à une edge function
        const functionUrl = urlMatch[1];
        
        // Appeler la fonction directement
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: '{}'
        });

        if (!response.ok) {
          throw new Error(`Erreur HTTP: ${response.status}`);
        }

        const result = await response.json();

        toast({
          title: "Test réussi",
          description: `Edge function exécutée avec succès`,
        });
      } else {
        // C'est une commande SQL directe - on ne peut pas la tester directement
        toast({
          title: "Information",
          description: "Ce cron job exécute une commande SQL directe. Il ne peut pas être testé manuellement depuis l'interface. Vérifiez l'historique d'exécution pour voir les résultats.",
          variant: "default"
        });
      }

    } catch (error: any) {
      toast({
        title: "Erreur de test",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setTestingJob(null);
    }
  };

  const updateCronJob = async () => {
    try {
      // Vérifier le mot de passe
      if (!password) {
        toast({
          title: "Mot de passe requis",
          description: "Veuillez entrer votre mot de passe pour confirmer",
          variant: "destructive"
        });
        return;
      }

      // Vérifier le mot de passe avec Supabase
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        throw new Error("Utilisateur non connecté");
      }

      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: password
      });

      if (authError) {
        toast({
          title: "Mot de passe incorrect",
          description: "Le mot de passe saisi est incorrect",
          variant: "destructive"
        });
        return;
      }

      // Mettre à jour le cron job
      const { error } = await supabase.rpc('update_cron_job', {
        job_name: editForm.jobname,
        new_schedule: editForm.schedule,
        new_command: editForm.command
      });
      
      if (error) throw error;
      
      toast({
        title: "Succès",
        description: "Cron job mis à jour avec succès",
      });
      
      setEditDialog(false);
      setPassword('');
      await fetchCronJobs();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const openDeleteDialog = (job: CronJob) => {
    setSelectedJob(job);
    setDeletePassword('');
    setDeleteDialog(true);
  };

  const deleteCronJob = async () => {
    if (!selectedJob) return;

    try {
      // Vérifier le mot de passe
      if (!deletePassword) {
        toast({
          title: "Mot de passe requis",
          description: "Veuillez entrer votre mot de passe pour confirmer",
          variant: "destructive"
        });
        return;
      }

      // Vérifier le mot de passe avec Supabase
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        throw new Error("Utilisateur non connecté");
      }

      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: deletePassword
      });

      if (authError) {
        toast({
          title: "Mot de passe incorrect",
          description: "Le mot de passe saisi est incorrect",
          variant: "destructive"
        });
        return;
      }

      // Supprimer le cron job
      const { error } = await supabase.rpc('delete_cron_job', { job_name: selectedJob.jobname });
      
      if (error) throw error;
      
      toast({
        title: "Succès",
        description: "Cron job supprimé avec succès",
      });
      
      setDeleteDialog(false);
      setDeletePassword('');
      await fetchCronJobs();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const openEditDialog = (job: CronJob) => {
    setEditForm({
      jobname: job.jobname,
      schedule: job.schedule,
      command: job.command
    });
    
    // Trouver le preset correspondant
    const preset = SCHEDULE_PRESETS.find(p => p.value === job.schedule);
    setSchedulePreset(preset ? preset.value : 'custom');
    
    setSelectedJob(job);
    setPassword('');
    setEditDialog(true);
  };

  const handleSchedulePresetChange = (value: string) => {
    setSchedulePreset(value);
    if (value !== 'custom') {
      setEditForm({ ...editForm, schedule: value });
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCronJobs(), fetchCronHistory()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const getScheduleLabel = (schedule: string) => {
    const schedules: Record<string, string> = {
      '0 */2 * * *': 'Toutes les 2 heures',
      '0 1 * * *': 'Quotidien à 1h',
      '0 2 * * 0': 'Hebdomadaire (Dimanche 2h)',
      '0 3 * * *': 'Quotidien à 3h',
      '0 4 * * *': 'Quotidien à 4h',
      '0 5 * * *': 'Quotidien à 5h',
      '0 9 * * *': 'Quotidien à 9h'
    };
    return schedules[schedule] || schedule;
  };

  const getJobDescription = (jobname: string) => {
    const descriptions: Record<string, string> = {
      'remind-pending-orders': 'Rappels commandes en attente de paiement (2h) et annulation automatique (24h)',
      'expire-deals-daily': 'Expiration automatique des deals et publicités',
      'check-stock-daily': 'Vérification du stock et désactivation des produits rupture',
      'cleanup-sessions-daily': 'Nettoyage des sessions expirées',
      'update-product-stats-daily': 'Mise à jour des statistiques produits',
      'archive-old-orders-weekly': 'Archivage des anciennes commandes (>90 jours)',
      'send-subscription-expiry-reminders': 'Rappels d\'expiration des abonnements'
    };
    return descriptions[jobname] || 'Description non disponible';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-6 w-6 text-primary" />
                Gestion des Cron Jobs
              </CardTitle>
              <CardDescription>
                Configurez et surveillez les tâches automatisées de la plateforme
              </CardDescription>
            </div>
            <Button onClick={() => fetchCronJobs()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Planification</TableHead>
                <TableHead>État</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cronJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    Aucun cron job configuré
                  </TableCell>
                </TableRow>
              ) : (
                cronJobs.map((job) => (
                  <TableRow key={job.jobid}>
                    <TableCell className="font-medium">{job.jobname}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-md">
                      {getJobDescription(job.jobname)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {getScheduleLabel(job.schedule)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={job.active}
                          onCheckedChange={(checked) => toggleCronJob(job.jobname, checked)}
                        />
                        {job.active ? (
                          <Badge variant="default" className="gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Actif
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="gap-1">
                            <Pause className="h-3 w-3" />
                            Inactif
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => testCronJob(job.jobname, job.jobid)}
                          disabled={testingJob === job.jobid}
                          title={job.command.includes('net.http_post') ? 'Tester cette tâche' : 'Cette tâche SQL ne peut pas être testée manuellement'}
                        >
                          {testingJob === job.jobid ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(job)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedJob(job);
                            setHistoryDialog(true);
                          }}
                        >
                          <Activity className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(job)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Historique d'exécution */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-primary" />
                Historique d'exécution récent
              </CardTitle>
              <CardDescription className="mt-1">
                Dernières exécutions des tâches planifiées
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchCronHistory}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Rafraîchir
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {cronHistory.length === 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <p className="font-semibold mb-2">Aucun historique d'exécution disponible</p>
                <p className="text-sm">
                  Les cron jobs s'exécutent selon leur planification définie. L'historique apparaîtra ici après la première exécution de chaque tâche.
                </p>
                <ul className="text-sm mt-2 space-y-1 list-disc list-inside">
                  <li>Les tâches actives s'exécuteront automatiquement aux heures programmées</li>
                  <li>Vous pouvez tester manuellement certaines tâches avec le bouton ▶️ Play</li>
                  <li>L'historique se met à jour automatiquement au chargement de la page</li>
                </ul>
              </AlertDescription>
            </Alert>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job</TableHead>
                  <TableHead>Début</TableHead>
                  <TableHead>Fin</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Message</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cronHistory.slice(0, 10).map((run) => {
                  const job = cronJobs.find(j => j.jobid === run.jobid);
                  return (
                    <TableRow key={run.runid}>
                      <TableCell className="font-medium">
                        {job?.jobname || `Job #${run.jobid}`}
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(run.start_time).toLocaleString('fr-FR')}
                      </TableCell>
                      <TableCell className="text-sm">
                        {run.end_time ? new Date(run.end_time).toLocaleString('fr-FR') : '-'}
                      </TableCell>
                      <TableCell>
                        {run.status === 'succeeded' ? (
                          <Badge variant="default" className="gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Réussi
                          </Badge>
                        ) : run.status === 'failed' ? (
                          <Badge variant="destructive" className="gap-1">
                            <XCircle className="h-3 w-3" />
                            Échoué
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="gap-1">
                            <RefreshCw className="h-3 w-3" />
                            {run.status}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                        {run.return_message || '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog d'édition */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Modifier le cron job
            </DialogTitle>
            <DialogDescription>
              Modifiez la planification et la commande du cron job. Une confirmation par mot de passe est requise.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Nom du job */}
            <div>
              <Label>Nom du job</Label>
              <Input
                value={editForm.jobname}
                disabled
                className="font-mono bg-muted"
              />
            </div>

            {/* Planification avec presets */}
            <div className="space-y-3">
              <Label>Planification</Label>
              
              <Select value={schedulePreset} onValueChange={handleSchedulePresetChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionnez une fréquence" />
                </SelectTrigger>
                <SelectContent>
                  {SCHEDULE_PRESETS.map((preset) => (
                    <SelectItem key={preset.value} value={preset.value}>
                      <div className="flex flex-col">
                        <span className="font-medium">{preset.label}</span>
                        <span className="text-xs text-muted-foreground">{preset.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {schedulePreset === 'custom' && (
                <div>
                  <Label className="text-xs">Expression cron personnalisée</Label>
                  <Input
                    value={editForm.schedule}
                    onChange={(e) => setEditForm({ ...editForm, schedule: e.target.value })}
                    placeholder="0 */2 * * *"
                    className="font-mono"
                  />
                  <Alert className="mt-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      Format: <code>minute heure jour mois jour-semaine</code>
                      <br />
                      Exemples: <code>0 */2 * * *</code> (toutes les 2h), <code>0 3 * * *</code> (quotidien à 3h)
                    </AlertDescription>
                  </Alert>
                </div>
              )}
              
              {schedulePreset !== 'custom' && (
                <div className="bg-muted/50 p-3 rounded-md">
                  <p className="text-sm font-mono">{editForm.schedule}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Expression cron générée
                  </p>
                </div>
              )}
            </div>

            {/* Commande SQL */}
            <div>
              <Label>Commande SQL</Label>
              <Textarea
                value={editForm.command}
                onChange={(e) => setEditForm({ ...editForm, command: e.target.value })}
                rows={6}
                className="font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Commande SQL à exécuter (généralement un appel à net.http_post pour invoquer une edge function)
              </p>
            </div>

            {/* Confirmation par mot de passe */}
            <div className="border-t pt-4">
              <Alert>
                <Lock className="h-4 w-4" />
                <AlertDescription>
                  <Label htmlFor="password" className="text-sm font-semibold">
                    Confirmation requise
                  </Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Pour des raisons de sécurité, veuillez entrer votre mot de passe pour confirmer cette modification.
                  </p>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Entrez votre mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="max-w-sm"
                  />
                </AlertDescription>
              </Alert>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setEditDialog(false);
              setPassword('');
            }}>
              Annuler
            </Button>
            <Button onClick={updateCronJob} disabled={!password}>
              <Save className="h-4 w-4 mr-2" />
              Enregistrer les modifications
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de suppression */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Supprimer le cron job
            </DialogTitle>
            <DialogDescription>
              Cette action est irréversible. Le cron job sera définitivement supprimé.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Vous êtes sur le point de supprimer le cron job <strong>{selectedJob?.jobname}</strong>.
                <br />
                Cette action ne peut pas être annulée.
              </AlertDescription>
            </Alert>

            <div>
              <Label htmlFor="delete-password" className="text-sm font-semibold">
                Confirmation par mot de passe
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                Veuillez entrer votre mot de passe pour confirmer la suppression.
              </p>
              <Input
                id="delete-password"
                type="password"
                placeholder="Entrez votre mot de passe"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setDeleteDialog(false);
              setDeletePassword('');
            }}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={deleteCronJob} 
              disabled={!deletePassword}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
